import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Plus, Pencil, Trash2, Star, Home, Building, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL_OPTIONS = [
  { value: "Home", icon: Home },
  { value: "Work", icon: Building },
  { value: "Other", icon: MapPinned }
];

const emptyAddress = {
  label: "Home",
  first_name: "",
  last_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  county: "",
  postcode: "",
  country: "United Kingdom",
  phone: "",
  is_default: false
};

export const Addresses = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader, loading: authLoading } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [formData, setFormData] = useState(emptyAddress);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
      return;
    }

    if (isAuthenticated) {
      fetchAddresses();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses`, {
        headers: getAuthHeader()
      });
      setAddresses(response.data);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      toast.error("Failed to load addresses");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLabelChange = (value) => {
    setFormData(prev => ({ ...prev, label: value }));
  };

  const openAddDialog = () => {
    setEditingAddress(null);
    setFormData(emptyAddress);
    setDialogOpen(true);
  };

  const openEditDialog = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label,
      first_name: address.first_name,
      last_name: address.last_name,
      address_line1: address.address_line1,
      address_line2: address.address_line2 || "",
      city: address.city,
      county: address.county || "",
      postcode: address.postcode,
      country: address.country,
      phone: address.phone || "",
      is_default: address.is_default
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (address) => {
    setAddressToDelete(address);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.first_name || !formData.last_name || !formData.address_line1 || !formData.city || !formData.postcode) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      if (editingAddress) {
        await axios.put(`${API}/addresses/${editingAddress.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Address updated successfully");
      } else {
        await axios.post(`${API}/addresses`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Address added successfully");
      }
      setDialogOpen(false);
      fetchAddresses();
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error(error.response?.data?.detail || "Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!addressToDelete) return;
    
    try {
      await axios.delete(`${API}/addresses/${addressToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success("Address deleted successfully");
      setDeleteDialogOpen(false);
      setAddressToDelete(null);
      fetchAddresses();
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error(error.response?.data?.detail || "Failed to delete address");
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await axios.post(`${API}/addresses/${addressId}/set-default`, {}, {
        headers: getAuthHeader()
      });
      toast.success("Default address updated");
      fetchAddresses();
    } catch (error) {
      console.error("Error setting default:", error);
      toast.error("Failed to set default address");
    }
  };

  const getLabelIcon = (label) => {
    const option = LABEL_OPTIONS.find(opt => opt.value === label);
    return option ? option.icon : MapPinned;
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-secondary w-1/3" />
            <div className="h-32 bg-secondary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16" data-testid="addresses-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="mb-10 flex items-center justify-between">
          <div>
            <p className="label-style mb-2">My</p>
            <h1 className="section-title">Addresses</h1>
          </div>
          <Button 
            onClick={openAddDialog}
            className="rounded-full uppercase tracking-widest text-xs"
            data-testid="add-address-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Address
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-32 bg-secondary animate-pulse" />
            ))}
          </div>
        ) : addresses.length === 0 ? (
          <div className="text-center py-16 bg-secondary">
            <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <p className="text-muted-foreground mb-4">No saved addresses</p>
            <p className="text-sm text-muted-foreground mb-6">
              Add an address to make checkout faster
            </p>
            <Button 
              onClick={openAddDialog}
              className="rounded-full uppercase tracking-widest"
              data-testid="add-first-address-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Address
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {addresses.map((address) => {
              const LabelIcon = getLabelIcon(address.label);
              return (
                <div
                  key={address.id}
                  className={`border p-4 relative ${address.is_default ? 'border-primary' : 'border-border'}`}
                  data-testid={`address-card-${address.id}`}
                >
                  {address.is_default && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-primary">
                      <Star className="h-3 w-3 fill-current" />
                      Default
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3 mb-3">
                    <div className="h-8 w-8 bg-secondary flex items-center justify-center">
                      <LabelIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{address.label}</span>
                      </div>
                      <p className="font-medium">
                        {address.first_name} {address.last_name}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-sm text-muted-foreground space-y-0.5 mb-4">
                    <p>{address.address_line1}</p>
                    {address.address_line2 && <p>{address.address_line2}</p>}
                    <p>{address.city}{address.county && `, ${address.county}`}</p>
                    <p>{address.postcode}</p>
                    <p>{address.country}</p>
                    {address.phone && <p className="pt-1">{address.phone}</p>}
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(address)}
                      className="text-xs"
                      data-testid={`edit-address-${address.id}`}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(address)}
                      className="text-xs text-destructive hover:text-destructive"
                      data-testid={`delete-address-${address.id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                    {!address.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(address.id)}
                        className="text-xs ml-auto"
                        data-testid={`set-default-${address.id}`}
                      >
                        <Star className="h-3 w-3 mr-1" />
                        Set Default
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Address Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingAddress ? "Edit Address" : "Add New Address"}
              </DialogTitle>
              <DialogDescription>
                {editingAddress 
                  ? "Update your delivery address details"
                  : "Add a new delivery address to your account"
                }
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="label">Address Label</Label>
                <Select value={formData.label} onValueChange={handleLabelChange}>
                  <SelectTrigger className="mt-1" data-testid="label-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABEL_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="h-4 w-4" />
                          {opt.value}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address_line1">Address Line 1 *</Label>
                <Input
                  id="address_line1"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleInputChange}
                  className="mt-1"
                  placeholder="Street address"
                  required
                  data-testid="address-line1-input"
                />
              </div>

              <div>
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleInputChange}
                  className="mt-1"
                  placeholder="Apartment, suite, unit, etc. (optional)"
                  data-testid="address-line2-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="county">County</Label>
                  <Input
                    id="county"
                    name="county"
                    value={formData.county}
                    onChange={handleInputChange}
                    className="mt-1"
                    data-testid="county-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postcode">Postcode *</Label>
                  <Input
                    id="postcode"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    className="mt-1"
                    required
                    data-testid="postcode-input"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="mt-1"
                    data-testid="country-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1"
                  placeholder="For delivery updates (optional)"
                  data-testid="phone-input"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="rounded-full"
                  data-testid="save-address-btn"
                >
                  {saving ? "Saving..." : (editingAddress ? "Update Address" : "Add Address")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Delete Address</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this address? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            {addressToDelete && (
              <div className="py-4 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">
                  {addressToDelete.first_name} {addressToDelete.last_name}
                </p>
                <p>{addressToDelete.address_line1}</p>
                <p>{addressToDelete.city}, {addressToDelete.postcode}</p>
              </div>
            )}
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="rounded-full"
                data-testid="confirm-delete-btn"
              >
                Delete Address
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Addresses;
